// src/ReviewsPage.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Avatar,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { supabase } from './supabaseClient';
import { normalizeProfileImage } from './utils/imageUtils';

import { ink, status, surface } from './designTokens';
export default function ReviewsPage() {
  const [groupedReviews, setGroupedReviews] = useState([]);
  const [allGroupedReviews, setAllGroupedReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStylists, setExpandedStylists] = useState(new Set());

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all reviews
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (reviewsError) {
        console.error('Error fetching reviews:', reviewsError);
        setGroupedReviews([]);
        setLoading(false);
        return;
      }

      if (!reviews || reviews.length === 0) {
        setGroupedReviews([]);
        setLoading(false);
        return;
      }

      // Get unique stylist and client phone numbers
      const stylistPhones = [...new Set(reviews.map(r => r.stylist_phone_number).filter(Boolean))];
      const clientPhones = [...new Set(reviews.map(r => r.phone_number).filter(Boolean))];

      // Fetch stylist profiles
      const stylistMap = new Map();
      if (stylistPhones.length > 0) {
        const { data: stylists, error: stylistsError } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image')
          .in('phone_number', stylistPhones);

        if (!stylistsError && stylists) {
          stylists.forEach(stylist => {
            stylistMap.set(stylist.phone_number, {
              ...stylist,
              profile_image_url: normalizeProfileImage(stylist.profile_image),
            });
          });
        }
      }

      // Fetch client profiles
      const clientMap = new Map();
      if (clientPhones.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image')
          .in('phone_number', clientPhones);

        if (!clientsError && clients) {
          clients.forEach(client => {
            clientMap.set(client.phone_number, {
              ...client,
              profile_image_url: normalizeProfileImage(client.profile_image),
            });
          });
        }
      }

      // Group reviews by stylist
      const grouped = new Map();
      reviews.forEach(review => {
        const stylistPhone = review.stylist_phone_number;
        if (!stylistPhone) return;

        if (!grouped.has(stylistPhone)) {
          const stylist = stylistMap.get(stylistPhone) || {};
          grouped.set(stylistPhone, {
            stylistPhone,
            stylist: stylist,
            reviews: [],
            averageRating: 0,
            totalReviews: 0,
          });
        }

        const group = grouped.get(stylistPhone);
        const client = clientMap.get(review.phone_number) || {};
        
        group.reviews.push({
          ...review,
          client: client,
        });
        group.totalReviews++;
      });

      // Calculate average ratings for each stylist
      const groupedArray = Array.from(grouped.values()).map(group => {
        const totalRating = group.reviews.reduce((sum, r) => sum + (parseInt(r.rating) || 0), 0);
        group.averageRating = group.totalReviews > 0 ? (totalRating / group.totalReviews).toFixed(1) : 0;
        return group;
      });

      // Sort by average rating (descending)
      groupedArray.sort((a, b) => parseFloat(b.averageRating) - parseFloat(a.averageRating));

      setGroupedReviews(groupedArray);
      setAllGroupedReviews(groupedArray);
      
      // Expand all stylists by default
      setExpandedStylists(new Set(groupedArray.map(g => g.stylistPhone)));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter reviews based on search term
  useEffect(() => {
    if (!searchTerm) {
      setGroupedReviews(allGroupedReviews);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allGroupedReviews.map(group => {
      const matchingReviews = group.reviews.filter(review => {
        const stylistName = `${group.stylist?.first_name || ''} ${group.stylist?.last_name || ''}`.toLowerCase();
        const clientName = `${review.client?.first_name || ''} ${review.client?.last_name || ''}`.toLowerCase();
        const reviewText = (review.review || '').toLowerCase();
        return stylistName.includes(term) || clientName.includes(term) || reviewText.includes(term);
      });

      if (matchingReviews.length === 0) return null;

      return {
        ...group,
        reviews: matchingReviews,
      };
    }).filter(Boolean);

    setGroupedReviews(filtered);
  }, [searchTerm, allGroupedReviews]);

  // Hide keeps the row (the author still sees it, an admin can restore it);
  // remove deletes it for good. Both are recorded in the admin audit log.
  const setHidden = async (review, hide) => {
    let reason = null;
    if (hide) {
      reason = window.prompt('Why is this review being hidden? (kept on the record)');
      if (reason === null) return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('reviews')
      .update(hide
        ? { hidden_at: new Date().toISOString(), hidden_by: auth?.user?.email || null, hidden_reason: reason || null }
        : { hidden_at: null, hidden_by: null, hidden_reason: null })
      .eq('id', review.id);
    if (error) { window.alert(`Could not update the review: ${error.message}`); return; }
    await supabase.rpc('write_admin_audit', {
      p_action: hide ? 'review_hidden' : 'review_restored',
      p_entity_type: 'review', p_entity_id: review.id, p_payload: { reason },
    });
    fetchData();
  };

  const removeReview = async (review) => {
    if (!window.confirm('Permanently delete this review? Hiding it is reversible; this is not.')) return;
    const { error } = await supabase.from('reviews').delete().eq('id', review.id);
    if (error) { window.alert(`Could not delete the review: ${error.message}`); return; }
    await supabase.rpc('write_admin_audit', {
      p_action: 'review_deleted', p_entity_type: 'review', p_entity_id: review.id,
      p_payload: { stylist: review.stylist_phone_number, rating: review.rating },
    });
    fetchData();
  };

  const handleToggleStylist = (stylistPhone) => {
    const newExpanded = new Set(expandedStylists);
    if (newExpanded.has(stylistPhone)) {
      newExpanded.delete(stylistPhone);
    } else {
      newExpanded.add(stylistPhone);
    }
    setExpandedStylists(newExpanded);
  };

  const renderStars = (rating) => {
    const stars = [];
    const ratingNum = parseInt(rating) || 0;
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Box
          key={i}
          component="span"
          sx={{
            color: i <= ratingNum ? status.warning : surface.line,
            display: 'inline-block',
          }}
        >
          {i <= ratingNum ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
        </Box>
      );
    }
    return <Box sx={{ display: 'flex', gap: 0.25 }}>{stars}</Box>;
  };

  const getStylistName = (group) => {
    if (group.stylist?.first_name || group.stylist?.last_name) {
      return `${group.stylist.first_name || ''} ${group.stylist.last_name || ''}`.trim();
    }
    return group.stylistPhone || 'Unknown Stylist';
  };

  const getClientName = (review) => {
    if (review.client?.first_name || review.client?.last_name) {
      return `${review.client.first_name || ''} ${review.client.last_name || ''}`.trim();
    }
    return review.phone_number || 'Unknown Client';
  };

  return (
    <Box sx={{ p: 4, bgcolor: surface.page, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Reviews & Comments
        </Typography>
        <Typography variant="body2" sx={{ color: ink[500] }}>
          Manage and view all reviews grouped by stylist
        </Typography>
      </Box>

      {/* Search and Actions */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${surface.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: ink[300],
              fontSize: 20,
            }}
          />
          <TextField
            placeholder="Search by stylist, client, or review..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                pl: 4.5,
                '& fieldset': {
                  borderColor: surface.line,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{
              borderColor: surface.line,
              color: ink[700],
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { borderColor: ink[300], bgcolor: surface.page },
            }}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            sx={{
              borderColor: surface.line,
              color: ink[700],
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { borderColor: ink[300], bgcolor: surface.page },
            }}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Reviews Grouped by Stylist */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : groupedReviews.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px solid ${surface.line}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" sx={{ color: ink[500] }}>
            No reviews found
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupedReviews.map((group) => {
            const isExpanded = expandedStylists.has(group.stylistPhone);
            const stylistName = getStylistName(group);
            const stylistImage = group.stylist?.profile_image_url;

            return (
              <Accordion
                key={group.stylistPhone}
                expanded={isExpanded}
                onChange={() => handleToggleStylist(group.stylistPhone)}
                sx={{
                  border: `1px solid ${surface.line}`,
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: surface.page,
                    borderRadius: 2,
                    '&:hover': { bgcolor: surface.sunken },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar
                      src={stylistImage}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: ink[900],
                        fontSize: '1.2rem',
                      }}
                    >
                      {group.stylist?.first_name?.[0] || group.stylist?.last_name?.[0] || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
                        {stylistName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {renderStars(group.averageRating)}
                        <Typography variant="body2" sx={{ color: ink[500], ml: 0.5 }}>
                          {group.averageRating} ({group.totalReviews} {group.totalReviews === 1 ? 'review' : 'reviews'})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: surface.page }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Client
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Rating
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Review
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Moderation
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.reviews.map((review, idx) => {
                          const clientName = getClientName(review);
                          const clientImage = review.client?.profile_image_url;

                          return (
                            <TableRow key={review.id || idx} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    src={clientImage}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: surface.line,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {review.client?.first_name?.[0] || review.client?.last_name?.[0] || '?'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {clientName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: ink[500], fontSize: '0.7rem' }}>
                                      {review.client?.email || review.phone_number}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {renderStars(review.rating)}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: ink[700], whiteSpace: 'pre-wrap' }}>
                                  {review.review || '—'}
                                </Typography>
                                {review.hidden_at && (
                                  <Chip size="small" label={`Hidden${review.hidden_reason ? `: ${review.hidden_reason}` : ''}`} sx={{ mt: 0.5 }} />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: ink[500] }}>
                                  {review.created_at
                                    ? new Date(review.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Button size="small" onClick={() => setHidden(review, !review.hidden_at)} sx={{ textTransform: 'none' }}>
                                  {review.hidden_at ? 'Restore' : 'Hide'}
                                </Button>
                                <Button size="small" color="error" onClick={() => removeReview(review)} sx={{ textTransform: 'none' }}>
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

